import React, { useState, useContext } from 'react';
import {
  Button,
  TextField,
  Typography,
  Container,
  Box,
  Avatar,
  useMediaQuery,
  IconButton,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  createTheme,
  ThemeProvider,
  responsiveFontSizes
} from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { changeLanguage } from '../i18n';
import ClubThemeContext from '../contexts/ClubThemeContext';

const APP_VERSION = '2.0.0';

// Créer et optimiser le thème pour le responsive
let theme = createTheme({
  palette: {
    primary: {
      main: '#F25F29',
    },
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: '16px',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            '@media (max-width:600px)': {
              fontSize: '1rem',
              height: '48px',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: '12px',
            fontSize: '1rem',
          },
        },
      },
    },
  },
});

theme = responsiveFontSizes(theme);

function LoginPage({ onLogin }) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Context pour le thème du club
  const clubThemeContext = useContext(ClubThemeContext);

  const avatarPath = process.env.REACT_APP_SECTION === 'hommes'
    ? '/assets/avatar_homme.png'
    : '/assets/avatar_femme.png';

  // Détecter le club via l'URL pour afficher le logo et les couleurs correspondants
  const currentUrl = window.location.href.toLowerCase();
  const isKansas = currentUrl.includes('kansas');
  const isMetropolitans = currentUrl.includes('metropolitans');
  const isByers = currentUrl.includes('byers');
  const isExternalClub = isKansas || isMetropolitans || isByers;

  const logoPath = isKansas ? '/assets/kansas.png'
    : isMetropolitans ? '/assets/metropolitans.png'
    : isByers ? '/assets/byers.png'
    : '/assets/valenciaapp.png';
  const primaryColor = isKansas ? '#320b57'
    : isMetropolitans ? '#E8C840'
    : isByers ? '#E8C840'
    : '#F25F29';

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          login,
          password,
          section: process.env.REACT_APP_SECTION,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        const userData = {
          nom: data.user.nom,
          prenom: data.user.prenom,
          avatar: data.user.avatar,
          role: data.user.role,
          id: data.user.id,
          club_id: data.user.club_id,
          club_name: data.user.club?.name || '',
          is_demo: data.user.is_demo || false,
          can_install_instat_extension: data.user.can_install_instat_extension || false,
        };
        localStorage.setItem('user', JSON.stringify(userData));

        // Mode demo: ajouter classe CSS pour flouter les photos
        if (data.user.is_demo) {
          document.body.classList.add('demo-mode');
        }

        // Stocker le club_id et club_slug pour le multi-tenant
        if (data.user.club_id) {
          localStorage.setItem('clubId', data.user.club_id.toString());
        }
        if (data.user.club?.slug) {
          localStorage.setItem('clubSlug', data.user.club.slug);
        }
        if (data.user.club?.name) {
          localStorage.setItem('clubName', data.user.club.name);
        }

        // Stocker et appliquer la langue du club
        if (data.user.club?.language) {
          localStorage.setItem('language', data.user.club.language);
          changeLanguage(data.user.club.language);
        }

        // Stocker et appliquer le thème du club (couleurs et logo)
        if (clubThemeContext) {
          clubThemeContext.updateTheme({
            primaryColor: data.user.club?.primaryColor || '#F25F29',
            secondaryColor: data.user.club?.secondaryColor || '#FF7043',
            logo: data.user.club?.logo || null,
            clubName: data.user.club?.name || 'Valencia App'
          });
        }

        // Stocker la config du club (URLs légales, etc.)
        if (data.user.club?.config) {
          localStorage.setItem('clubConfig', JSON.stringify(data.user.club.config));
        }

        // Stocker aussi le token directement pour l'intercepteur Axios
        localStorage.setItem('token', data.token);

        onLogin(data.token);

        // Redirection conditionnelle selon le rôle  
        if (data.user.role === 'entrenador' || data.user.role === 'scout interne' || data.user.role === 'scout externe' || data.user.role === 'scout interne cantera' || data.user.role === 'scout externe cantera') {
          navigate('/jugadores');
        } else if (data.user.role === 'PDP') {
          navigate('/tecnification');
        } else {
          navigate('/menu');
        }
      } else {
        setError(data.message || 'Nombre de usuario o contraseña incorrecta');
      }
    } catch (err) {
      setError('Error de conexión. Por favor, inténtelo de nuevo.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: primaryColor,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: isMobile ? '16px' : '24px'
        }}
      >
        <Container
          maxWidth="xs"
          sx={{
            backgroundColor: '#ffffff',
            padding: isMobile ? '16px' : '24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            width: '100%'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
            component="form"
            onSubmit={handleLogin}
          >
            <img
              src={logoPath}
              alt={isExternalClub ? "Club Logo" : "Valencia App Logo"}
              style={{
                width: isMobile ? '120px' : '150px',
                marginBottom: '16px',
                height: 'auto'
              }}
            />
            
            {!isExternalClub && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 2 : 1
                }}
              >
                <Avatar
                  src={avatarPath}
                  alt="Avatar"
                  sx={{
                    width: isMobile ? 80 : 100,
                    height: isMobile ? 80 : 100
                  }}
                />
              </Box>
            )}

            {error && (
              <Typography 
                color="error" 
                variant="body2" 
                sx={{ 
                  mb: 2,
                  textAlign: 'center',
                  width: '100%'
                }}
              >
                {error}
              </Typography>
            )}

            <TextField
              margin="normal"
              fullWidth
              id="login"
              label="Login"
              name="login"
              autoComplete="username"
              autoFocus
              variant="outlined"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              disabled={isLoading}
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: '8px'
                }
              }}
            />

            <TextField
              margin="normal"
              fullWidth
              name="password"
              label={isExternalClub ? "Password" : "Contraseña"}
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: '8px'
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                mt: 3,
                mb: 2,
                height: '48px',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '1.1rem',
                backgroundColor: primaryColor,
                '&:hover': {
                  backgroundColor: primaryColor,
                  opacity: 0.9
                }
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                isExternalClub ? 'Log in' : 'Iniciar sesión'
              )}
            </Button>

            <Typography
              variant="caption"
              sx={{
                mt: 2,
                color: 'text.secondary',
                fontSize: '0.75rem'
              }}
            >
              v{APP_VERSION}
            </Typography>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default LoginPage;
